import React, { useEffect, useRef, useState } from 'react';

/**
 * OpenTable reservation widget — official inline embed with a graceful fallback.
 * Loads the OpenTable loader script with the given restaurant ID. If the external
 * script fails to inject its iframe (blocked / network error), we show a direct
 * "Reserve on OpenTable" link so guests always have a booking path.
 *
 * The OpenTable loader can inject more than one iframe (notably under React
 * StrictMode's mount → unmount → mount cycle). We fully wipe the container on
 * cleanup and keep only the first iframe so exactly ONE widget is ever shown.
 *
 * Props:
 *  - rid: OpenTable restaurant ID (string/number)
 *  - theme: 'standard' | 'tall' | 'wide'
 */
const OpenTableWidget = ({ rid, theme = 'standard' }) => {
  const containerRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!rid || !container) return undefined;
    setFailed(false);
    container.innerHTML = '';

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src =
      `//www.opentable.com/widget/reservation/loader?rid=${rid}` +
      `&type=${theme}&theme=tall&color=1&dark=true&iframe=true&domain=com` +
      `&lang=en-US&newtab=false&ot_source=Restaurant%20website&cfe=true`;
    script.onerror = () => setFailed(true);
    container.appendChild(script);

    // Guarantee a single widget: drop any extra iframes the loader injects.
    const dedupe = () => {
      const iframes = container.querySelectorAll('iframe');
      for (let i = 1; i < iframes.length; i += 1) iframes[i].remove();
    };
    const observer = new MutationObserver(dedupe);
    observer.observe(container, { childList: true, subtree: true });

    // Fallback: if no iframe appears in a few seconds, surface the direct link.
    const timer = setTimeout(() => {
      if (container.querySelector('iframe') === null) setFailed(true);
    }, 4500);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      container.innerHTML = '';
    };
  }, [rid, theme]);

  return (
    <div className="w-full flex flex-col items-center gap-4">
      <div ref={containerRef} data-testid="opentable-widget" className="opentable-widget w-full flex justify-center" />
      {failed && (
        <a
          href={`https://www.opentable.com/restaurant/profile/${rid}`}
          target="_blank"
          rel="noreferrer"
          data-testid="opentable-fallback-link"
          className="inline-flex items-center gap-2 px-8 py-4 bg-[#942222] hover:bg-[#b13030] text-[#f1ead9] text-[11px] tracking-[0.28em] uppercase font-semibold transition-colors"
        >
          Reserve on OpenTable
        </a>
      )}
    </div>
  );
};

export default OpenTableWidget;
